import { CommonModule } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  computed,
  inject,
  input,
  signal,
} from '@angular/core';
import { ButtonLink, WwwDirectusService, ShowCTABlock } from 'www-cms';
import { rxResource } from '@angular/core/rxjs-interop';
import { of, catchError } from 'rxjs';
import { ButtonLinkComponent } from 'building-blocks/button-link/button-link.component';
import { BaseBuildingBlockComponent } from '../components/base-building-block.component';
import { LastItemBgClass } from '../../shell/shell.service';
import { StarComponent } from '../../shared/components/star/star.component';
import { SafePipe } from 'common';
import { ModalComponent } from 'ui-elements/modal';
import { MonthViewBaseComponent } from 'building-blocks/month-view-base/month-view-base.component';
import { ButtonComponent } from 'ui-elements/button';
import {
  trigger,
  transition,
  query,
  style,
  animate,
} from '@angular/animations';
import { DirectusDirective } from 'directus';

@Component({
  selector: 'ss-www-show-cta-block',
  imports: [
    CommonModule,
    ButtonLinkComponent,
    StarComponent,
    SafePipe,
    ModalComponent,
    MonthViewBaseComponent,
    ButtonComponent,
    DirectusDirective,
  ],
  templateUrl: './show-cta-block.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  animations: [
    trigger('modalAnimation', [
      transition(':enter', [
        query(
          '.modal',
          [
            style({ opacity: 0, transform: 'scale3d(0.3, 0.3, 0.3)' }),
            animate(
              '0.2s ease-in-out',
              style({ opacity: 1, transform: 'scale3d(1, 1, 1)' })
            ),
          ],
          { optional: true }
        ),
      ]),
    ]),
  ],
})
export class ShowCTABlockComponent extends BaseBuildingBlockComponent {
  private readonly directusService = inject(WwwDirectusService);

  showCTABlockData = input.required<ShowCTABlock>();

  showCalendarModal = signal(false);

  // Reactive resource for show data
  private readonly showResource = rxResource({
    params: () => this.showCTABlockData().show?.showCode,
    stream: ({ params: showCode }) => {
      if (!showCode) {
        return of(null);
      }
      return this.directusService.getShow(showCode).pipe(
        catchError((err) => {
          console.error('Failed to load show data:', err);
          return of(null);
        })
      );
    },
  });

  readonly showData = computed(() => this.showResource.value());
  readonly loading = computed(() => this.showResource.isLoading());
  readonly error = computed(() => this.showResource.error());

  dynamicButtons = computed(() => {
    const show = this.showData();
    const isLoading = this.loading();

    if (isLoading || !show) {
      return [];
    }

    const buttons: ButtonLink[] = [];

    // If booking is available, add "Get Tickets" button
    if (show.bookingAvailable) {
      buttons.push({
        id: 1,
        text: 'Get Tickets',
        buttonStyle: 'filled',
        buttonColor: 'principal',
        url: `/ticketing`,
      });
    }

    // If show trailer exists, add "Watch Trailer" button
    if (show.trailerVimeoID) {
      buttons.push({
        id: 2,
        text: 'Watch Trailer',
        buttonStyle: 'outlined',
        buttonColor: 'principal',
        vimeoVideoId: show.trailerVimeoID,
      });
    }

    if (show.trailerYouTubeId) {
      buttons.push({
        id: 4,
        text: 'Watch Trailer',
        buttonStyle: 'outlined',
        buttonColor: 'principal',
        youtubeVideoId: show.trailerYouTubeId,
      });
    }

    // If booking is available, add "View Show Calendar" button
    if (show.bookingAvailable) {
      buttons.push({
        id: 3,
        text: 'View Show Calendar',
        buttonStyle: 'outlined',
        buttonColor: 'principal',
        url: 'SHOW_CALENDAR_MODAL',
      });
    }

    return buttons;
  });

  trackButton(index: number, button: ButtonLink) {
    return button.id;
  }

  openCalendarModal() {
    this.showCalendarModal.set(true);
  }

  closeCalendarModal() {
    this.showCalendarModal.set(false);
  }

  override getBgClass(): LastItemBgClass {
    return this.showCTABlockData().darkBackground
      ? 'bg-ss-www-background'
      : 'bg-white';
  }
}
